import { Alert, Badge, Tooltip } from '@/components/ui'
import { formatPercent } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import type { ClockCheck } from '@/types'

/**
 * Surfaces how much of a transcript's speaker attribution can be trusted.
 *
 * This is the product's single most dangerous failure: when the caption clock
 * and the audio clock disagree, the merge does not fail — it names everybody
 * plausibly and wrongly. A wrongly attributed action item is an email telling a
 * named colleague they owe a deliverable they never agreed to. So:
 *
 *  - `suspect`  -> a persistent, unmissable banner. Never a toast, never a
 *                  tooltip, and it must appear on every surface that shows a
 *                  speaker name (transcript, summary, action items, share page).
 *  - `no-evidence` -> honest and quieter: nothing was attributed at all, which
 *                  is visibly incomplete rather than confidently wrong.
 *  - `ok` with a low namedFraction -> an inline chip, because part of the
 *                  meeting is still unnamed even though the clocks agree.
 */
export function AttributionNotice({ check }: { check: ClockCheck | null }) {
  const t = useT()
  if (!check) return null

  if (check.verdict === 'suspect') {
    return (
      <Alert tone="attribution" title={t('attribution.suspectTitle')}>
        {t('attribution.suspectBody')}
      </Alert>
    )
  }

  if (check.verdict === 'no-evidence') {
    return (
      <Alert tone="info" title={t('attribution.noNamesTitle')}>
        {t('attribution.noNamesBody')}
      </Alert>
    )
  }

  if (check.namedFraction < 0.85) {
    return (
      <Tooltip
        content={t('attribution.notAttributedTip', { percent: formatPercent(1 - check.namedFraction) })}
      >
        <Badge tone="warning">{t('attribution.attributed', { percent: formatPercent(check.namedFraction) })}</Badge>
      </Tooltip>
    )
  }

  return null
}
